<template>
  <div class="bg-white rounded-xl border border-gray-200/60 mb-6 card-shadow">
    <div class="flex items-end gap-3 px-4 pt-3 pb-2 flex-wrap">
      <!-- Date range — reuses the same picker and preset chips as
           AnalyticsToolbar; styles are non-scoped and shared. -->
      <div class="shrink-0">
        <div class="w-[330px]">
          <VueDatePicker
            v-model="dateRangeValue"
            range
            :multi-calendars="true"
            :enable-time-picker="true"
            :time-picker-inline="true"
            :preset-dates="presetDates"
            :max-date="maxDate"
            :auto-apply="false"
            format="yyyy-MM-dd HH:mm"
            placeholder="Select date range"
            input-class-name="custom-dp-input"
            :menu-class-name="menuClassName"
            :clearable="false"
            position="left"
            teleport="body"
            @update:model-value="onDateRangeChange"
          >
            <template #input-icon>
              <svg class="w-4 h-4 text-gray-400 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </template>
            <template #lmp-preset="{ label, value, presetDate }">
              <div
                :class="['lmp-preset-item', { 'lmp-preset-active': isPresetActive(label) }]"
                @click="onPresetClick(label, value, presetDate)"
              >
                <span class="lmp-preset-dot"></span>
                <span class="lmp-preset-label">{{ label }}</span>
              </div>
            </template>
            <template #action-row="{ internalModelValue, selectDate, closePicker }">
              <div class="lmp-action-row">
                <div class="lmp-action-info">
                  <svg class="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>Max allowed: <strong>{{ maxDateLabel }}</strong></span>
                </div>
                <div class="lmp-action-buttons">
                  <button class="lmp-btn-cancel" type="button" @click="closePicker">Cancel</button>
                  <button
                    class="lmp-btn-apply"
                    type="button"
                    :disabled="!isRangeValid(internalModelValue)"
                    @click="selectDate(internalModelValue)"
                  >Apply</button>
                </div>
              </div>
            </template>
          </VueDatePicker>
        </div>
      </div>

      <!-- App-name cross-section — options loaded from analytics_dimensions
           (same source AnalyticsToolbar uses). Remote search so any
           app the writer has ever seen is discoverable via typeahead. -->
      <div class="shrink-0">
        <div class="w-[180px]">
          <SearchSelect
              ref="appSelect"
              v-model="selectedAppName"
              :options="appNameOptions"
              placeholder="All apps"
              search-placeholder="Search apps..."
              label-key="name"
              value-key="id"
              :icon="appIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onAppNamePicked"
              @search="onAppNameSearch"
          />
        </div>
      </div>

      <!-- Target type — Live vs Media. Segmented buttons. Drives
           the conditional selector below (live+show OR media). -->
      <div class="shrink-0">
        <div class="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 h-[36px]">
          <button
            v-for="opt in targetTypeOptions"
            :key="opt.id"
            type="button"
            @click="onTargetTypePicked(opt.id)"
            :class="[
              'px-3 h-[28px] rounded-md text-[12px] font-medium transition-colors flex items-center gap-1.5',
              selectedTargetType === opt.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >
            <span v-html="opt.icon"></span>
            {{ opt.name }}
          </button>
        </div>
      </div>

      <!-- Live + Show selectors — only when Target = Live. -->
      <template v-if="selectedTargetType === 'live'">
        <div class="shrink-0">
          <div class="w-[220px]">
            <SearchSelect
              ref="liveSelect"
              v-model="selectedLive"
              :options="lives"
              placeholder="All lives"
              search-placeholder="Search lives..."
              label-key="name"
              value-key="_id"
              :icon="liveIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onLivePicked"
              @search="onLiveSearch"
            />
          </div>
        </div>
        <div v-if="selectedLive" class="shrink-0">
          <div class="w-[180px]">
            <SearchSelect
              ref="showSelect"
              v-model="selectedShow"
              :options="showOptions"
              placeholder="All shows"
              search-placeholder="Search shows..."
              label-key="title"
              value-key="id"
              :icon="showIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onShowPicked"
              @search="onShowSearch"
            />
          </div>
        </div>
      </template>

      <!-- Media selector — only when Target = Media. -->
      <template v-if="selectedTargetType === 'media'">
        <div class="shrink-0">
          <div class="w-[220px]">
            <SearchSelect
              ref="mediaSelect"
              v-model="selectedMedia"
              :options="medias"
              placeholder="All medias"
              search-placeholder="Search medias..."
              label-key="name"
              value-key="_id"
              :icon="mediaIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onMediaPicked"
              @search="onMediaSearch"
            />
          </div>
        </div>
      </template>

      <div class="flex-1"></div>

      <!-- Ad-type segmented selector — All / Preroll / Midroll.
           Sits right before Search so it reads as the last cross-cut
           the user picks. Clicking a button also runs the search
           immediately (unlike other filters, which stage into
           `pending` and require a Search click). Ad type is a
           quick-toggle: users flip it repeatedly while scanning
           results and don't expect to press Search each time. -->
      <div class="shrink-0">
        <div class="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 h-[36px]">
          <button
            v-for="opt in adTypeOptions"
            :key="opt.id || 'all'"
            type="button"
            @click="onAdTypePicked(opt.id)"
            :class="[
              'px-3 h-[28px] rounded-md text-[12px] font-medium transition-colors',
              selectedAdType === opt.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >{{ opt.name }}</button>
        </div>
      </div>

      <!-- Search button — applies pending → applied. -->
      <div class="shrink-0">
        <button
          @click="onSearch"
          :disabled="loading || !canSearch"
          :class="[
            'h-[36px] px-5 rounded-lg text-[13px] font-medium transition-all duration-150 inline-flex items-center gap-2',
            canSearch && !loading
              ? 'bg-[var(--color-primary)] text-white hover:opacity-90 cursor-pointer shadow-sm'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          ]"
        >
          <svg v-if="loading" class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
            <path d="M22 12a10 10 0 01-10 10" stroke-linecap="round"/>
          </svg>
          <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <span>{{ loading ? 'Loading…' : 'Search' }}</span>
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import SearchSelect from '../../../components/SearchSelect.vue'
import api from '../../../services/api.js'

/**
 * Dedicated toolbar for the Ads analytics view.
 *
 * Filter shape:
 *   - Date range (same picker + presets as AnalyticsToolbar)
 *   - Ad type: All / Preroll (IMA) / SSAI  → `?pipeline=...`
 *   - Target type: Live | Media radio       → drives the selector below
 *   - When Live  → Live + Show selectors    → `?liveId=...&showId=...`
 *   - When Media → Media selector           → `?mediaId=...`
 *   - App name  → `?appName=...`
 *
 * Shares the `analytics` Vuex store with AnalyticsToolbar (pending /
 * applied / dirty), so going Ads → Overview → Ads preserves the date
 * window. Ads-specific fields (`adType`, `targetType`) live in the
 * same store under their own keys.
 */
export default {
  name: 'AdsToolbar',
  components: { VueDatePicker, SearchSelect },

  props: {
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      dateRangeValue: null,
      activePresetLabel: 'Today',
      now: new Date(),
      _nowTimer: null,

      selectedAdType:     null,
      selectedTargetType: 'live',
      selectedLive:    '',
      selectedShow:    '',
      selectedMedia:   '',
      selectedAppName: '',

      lives: [],
      shows: [],
      medias: [],
      apps: [],

      liveIcon:  '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>',
      showIcon:  '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>',
      mediaIcon: '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/></svg>',
      appIcon:   '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>'
    }
  },

  computed: {
    ...mapGetters('analytics', ['pending', 'adsPanel']),

    maxDate() { return new Date(this.now.getTime() - 60 * 1000) },
    maxDateLabel() {
      return this.maxDate.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit'
      })
    },

    /**
     * Same nine presets as the shared toolbar so the Ads view feels
     * native to the section. Includes a Custom entry that unlocks
     * the calendar grids when the user wants a non-preset window.
     */
    presetDates() {
      const top = this.maxDate
      const hour = 60 * 60 * 1000
      const day  = 24 * hour
      const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
      const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x }
      const yesterday  = new Date(top.getTime() - day)
      const startOfThisMonth = new Date(top.getFullYear(), top.getMonth(), 1)
      const startOfLastMonth = new Date(top.getFullYear(), top.getMonth() - 1, 1)
      const endOfLastMonth   = new Date(top.getFullYear(), top.getMonth(), 0, 23, 59, 59, 999)
      const startOfLastYear  = new Date(top.getFullYear() - 1, 0, 1)
      const endOfLastYear    = new Date(top.getFullYear() - 1, 11, 31, 23, 59, 59, 999)
      const slot = 'lmp-preset'
      return [
        { slot, label: 'Last Hour',     value: [new Date(top.getTime() - hour),     top] },
        { slot, label: 'Last 6 Hours',  value: [new Date(top.getTime() - 6 * hour), top] },
        { slot, label: 'Today',         value: [startOfDay(top),                    top] },
        { slot, label: 'Last 24 Hours', value: [new Date(top.getTime() - day),      top] },
        { slot, label: 'Yesterday',     value: [startOfDay(yesterday), endOfDay(yesterday)] },
        { slot, label: 'Last 7 Days',   value: [new Date(top.getTime() - 7 * day),  top] },
        { slot, label: 'Last 30 Days',  value: [new Date(top.getTime() - 30 * day), top] },
        { slot, label: 'This Month',    value: [startOfThisMonth, top] },
        { slot, label: 'Last Month',    value: [startOfLastMonth, endOfLastMonth] },
        { slot, label: 'Last Year',     value: [startOfLastYear,  endOfLastYear] },
        { slot, label: 'Custom',        value: this.dateRangeValue }
      ]
    },

    menuClassName() {
      const lock = this.activePresetLabel !== 'Custom' ? 'lmp-mode-locked' : ''
      return `custom-dp-menu lmp-range-menu ${lock}`.trim()
    },

    canSearch() {
      const r = this.dateRangeValue
      if (!Array.isArray(r) || r.length !== 2 || !r[0] || !r[1]) return false
      const max = this.maxDate
      return new Date(r[0]) <= max && new Date(r[1]) <= max
    },

    adTypeOptions() {
      return [
        { id: null,   name: 'All' },
        { id: 'ima',  name: 'Preroll' },
        { id: 'ssai', name: 'Midroll' }
      ]
    },

    targetTypeOptions() {
      return [
        { id: 'live',  name: 'Live',
          icon: '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>' },
        { id: 'media', name: 'Media',
          icon: '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/></svg>' }
      ]
    },

    showOptions() {
      return [{ id: '', title: 'All shows' }, ...this.shows]
    },

    // Dynamic — populated from /analytics/dimensions?type=app (same
    // source AnalyticsToolbar uses). The hardcoded LaMusicaWeb /
    // LaMusicaAndroid / LaMusicaIOS / LaMusicaTV list is gone: new
    // apps (LaMusicaWebTest, partner apps, etc.) show up automatically
    // as soon as the writer sees a session with that app_name.
    appNameOptions() {
      return [{ id: '', name: 'All apps' }, ...this.apps]
    }
  },

  watch: {
    'pending.liveId'(v)     { if (v !== this.selectedLive)       this.selectedLive       = v || '' },
    'pending.showId'(v)     { if (v !== this.selectedShow)       this.selectedShow       = v || '' },
    'pending.mediaId'(v)    { if (v !== this.selectedMedia)      this.selectedMedia      = v || '' },
    'pending.appName'(v)    { if (v !== this.selectedAppName)    this.selectedAppName    = v || '' },
    'pending.adType'(v)     { if (v !== this.selectedAdType)     this.selectedAdType     = v },
    'pending.targetType'(v) { if (v !== this.selectedTargetType) this.selectedTargetType = v || 'live' },

    'pending.dateFrom'(v, prev) {
      if (v && this.pending.dateTo && (v !== prev)) {
        this.dateRangeValue = [new Date(this.pending.dateFrom), new Date(this.pending.dateTo)]
      }
    }
  },

  mounted() {
    const max = this.maxDate
    let from = this.pending.dateFrom ? new Date(this.pending.dateFrom) : null
    let to   = this.pending.dateTo   ? new Date(this.pending.dateTo)   : null
    if (to && to > max) to = max
    if (from && from > max) from = max
    this.dateRangeValue = [from, to]
    this.activePresetLabel = this.pending.presetLabel || 'Custom'

    this.selectedAdType     = this.pending.adType     ?? null
    this.selectedTargetType = this.pending.targetType || 'live'
    this.selectedLive       = this.pending.liveId  || ''
    this.selectedShow       = this.pending.showId  || ''
    this.selectedMedia      = this.pending.mediaId || ''
    this.selectedAppName    = this.pending.appName || ''

    // App picker is always visible, so seed the app list up-front.
    if (!this.apps.length) this.loadApps()

    // Seed lists for whichever target is initially active. We
    // intentionally don't load both — the user can switch and we'll
    // lazy-load the other on first switch.
    if (this.selectedTargetType === 'live') {
      if (!this.lives.length)  this.loadLives()
      if (this.selectedLive && !this.shows.length) this.loadShows()
    } else if (this.selectedTargetType === 'media') {
      if (!this.medias.length) this.loadMedias()
    }

    this._nowTimer = setInterval(() => { this.now = new Date() }, 30 * 1000)
  },

  beforeUnmount() {
    if (this._nowTimer) { clearInterval(this._nowTimer); this._nowTimer = null }
  },

  methods: {
    // ---- Date picker ----
    onPresetClick(label, value, presetDate) {
      this.activePresetLabel = label
      if (label === 'Custom') return
      presetDate(value)
    },

    onDateRangeChange(value) {
      if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
        const max = this.maxDate
        let [from, to] = value.map(d => new Date(d))
        let clamped = false
        if (to > max)   { to = new Date(max);   clamped = true }
        if (from > max) { from = new Date(max); clamped = true }
        if (clamped) this.dateRangeValue = [from, to]
        this.$store.dispatch('analytics/setPending', {
          dateFrom: from,
          dateTo:   to,
          presetLabel: this.activePresetLabel
        })
      }
    },

    isPresetActive(label) { return this.activePresetLabel === label },

    isRangeValid(value) {
      if (!Array.isArray(value) || value.length !== 2) return false
      const [from, to] = value
      if (!from || !to) return false
      const max = this.maxDate
      return new Date(from) <= max && new Date(to) <= max && new Date(from) <= new Date(to)
    },

    // ---- Ad type ----
    // Auto-search on pick: unlike the other filters (which stage
    // pending and wait for the Search button), ad type is a quick-
    // toggle the user flips repeatedly while scanning results.
    // Requiring a Search click for each flip adds friction with no
    // benefit — no other input is being staged alongside it.
    onAdTypePicked(id) {
      if (id === this.selectedAdType) return
      this.selectedAdType = id
      this.$store.dispatch('analytics/setPending', { adType: id })
      this.$store.dispatch('analytics/apply')
      this.$emit('search')
    },

    // ---- Target type ----
    onTargetTypePicked(id) {
      if (id === this.selectedTargetType) return
      this.selectedTargetType = id
      // Clear the OTHER entity's selection so a stale liveId doesn't
      // leak into a media query and vice versa.
      const partial = { targetType: id }
      if (id === 'live')  { partial.mediaId = null; this.selectedMedia = '' }
      if (id === 'media') {
        partial.liveId = null; partial.showId = null
        this.selectedLive = ''; this.selectedShow = ''
      }
      this.$store.dispatch('analytics/setPending', partial)
      // Lazy-load the just-revealed entity list.
      if (id === 'live'  && !this.lives.length)  this.loadLives()
      if (id === 'media' && !this.medias.length) this.loadMedias()
    },

    // ---- Lives / Shows / Medias ----
    // All entity loaders read from /analytics/dimensions — same
    // source AnalyticsToolbar uses. Backed by analytics_dimensions
    // (writer stamps rows on session_started), so the dropdown
    // reflects entities the analytics actually observed rather
    // than the source-of-truth catalog which may include entries
    // with no traffic. Sorted by last_seen_at desc, capped at 50,
    // substring search on target_name.
    async loadLives(search = '') {
      try {
        const params = { type: 'live', limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        this.lives = items.map(d => ({ _id: d.target_id, name: d.target_name }))
        this.$refs.liveSelect?.setLoading(false)
      } catch {
        this.$refs.liveSelect?.setLoading(false)
      }
    },

    async loadShows(search = '') {
      if (!this.selectedLive) { this.shows = []; return }
      try {
        const params = { type: 'show', parentId: this.selectedLive, limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        this.shows = items.map(d => ({ id: d.target_id, title: d.target_name }))
      } catch {
        this.shows = []
      }
    },

    onLivePicked() {
      this.selectedShow = ''
      this.shows = []
      this.$store.dispatch('analytics/setPending', {
        liveId: this.selectedLive || null,
        showId: null
      })
      if (this.selectedLive) this.loadShows()
    },

    onLiveSearch(query) { this.loadLives(query) },

    onShowPicked() {
      this.$store.dispatch('analytics/setPending', { showId: this.selectedShow || null })
    },

    onShowSearch(query) { this.loadShows(query) },

    async loadMedias(search = '') {
      try {
        const params = { type: 'media', limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        this.medias = items.map(d => ({ _id: d.target_id, name: d.target_name }))
        this.$refs.mediaSelect?.setLoading(false)
      } catch {
        this.$refs.mediaSelect?.setLoading(false)
      }
    },

    onMediaPicked() {
      this.$store.dispatch('analytics/setPending', { mediaId: this.selectedMedia || null })
    },

    onMediaSearch(query) { this.loadMedias(query) },

    // ---- App name ----
    async loadApps(search = '') {
      try {
        const params = { type: 'app', limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        // For type=app the dimension's target_id and target_name are
        // both the SDK's app_name value. Store as { id, name } so the
        // SearchSelect config (value-key="id", label-key="name") reads
        // them directly.
        this.apps = items.map(d => ({ id: d.target_id, name: d.target_name }))
        this.$refs.appSelect?.setLoading(false)
      } catch {
        this.$refs.appSelect?.setLoading(false)
      }
    },

    onAppNamePicked() {
      this.$store.dispatch('analytics/setPending', { appName: this.selectedAppName || null })
    },

    onAppNameSearch(query) { this.loadApps(query) },

    // ---- Search ----
    onSearch() {
      if (!this.canSearch || this.loading) return
      this.$store.dispatch('analytics/apply')
      this.$emit('search')
    }
  }
}
</script>
